import type { FC } from "react";
import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { shortMonthAndYearWithDelimiter } from "@similarweb/pro-logic-components";

import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import { ETabs, YOUTUBE_KEYWORD_GENERATOR_ENDPOINT, YOUTUBE_KEYWORD_GENERATOR_EXCEL_ENDPOINT } from "./constants";
import { YoutubeKeywordGeneratorRelatedColumnsConfig } from "./YoutubeKeywordGeneratorTableColumns";
import { YoutubeKeywordGeneratorTableTop } from "./YoutubeKeywordGeneratorTableTop";

const fetchService = DefaultFetchService.getInstance();

type TabConfig = { endpointType: string; showScoreColumn: boolean; sortBy: string };
const tabConfigMap: Record<ETabs, TabConfig> = {
    [ETabs.PhraseMatch]: {
        endpointType: "Broad",
        showScoreColumn: false,
        sortBy: "volume",
    },
    [ETabs.RelatedKeywords]: {
        endpointType: "Related",
        showScoreColumn: true,
        sortBy: "score",
    },
};

const TOTAL_RECORDS_FIELD = "totalRecords";
const ROWS_COUNT = 100;
const PAGES_TO_FETCH = 3;

interface IYoutubeKeywordGeneratorTabTable {
    totalRecords: number;
    selectedTab: number;
    getDataCallback?: (totalRecords: number) => void;
    preventCountTracking?: boolean;
    onDataError?: (error: any) => void;
}

export const YoutubeKeywordGeneratorTabTable: FC<IYoutubeKeywordGeneratorTabTable> = (props) => {
    const { selectedTab, totalRecords } = props;
    const { params } = useCurrentStateAndParams();
    const tabConfig = tabConfigMap[selectedTab];
    const { keyword, country, webSource, multiIncludeExcludeKeywords, sort = tabConfig.sortBy, asc } = params;
    const [pageNumber, setPageNumber] = useState(0);
    const { to, from } = DurationService.getDurationData(BasicDurations.LAST_TWELVE_MONTHS).forAPI;
    const apiParams = {
        keyword,
        to,
        from,
        webSource,
        country,
        sort,
        asc,
        type: tabConfig.endpointType,
    };
    const paginationParams = {
        page: pageNumber,
        rowsPerPage: 300,
    };
    const requestBody = multiIncludeExcludeKeywords ? JSON.parse(multiIncludeExcludeKeywords) : [];

    const columns = YoutubeKeywordGeneratorRelatedColumnsConfig.getColumns(tabConfig.showScoreColumn, sort, asc);

    const getDataCallback = (data) => {
        const { getDataCallback: getDataCallbackProp } = props;
        getDataCallbackProp?.(data[TOTAL_RECORDS_FIELD]);
        return data;
    };

    const changePageCallback = (page) => {
        setPageNumber(page);
    };

    const onSort = ({ field, sortDirection }) => {
        proNavigator.updateParams({
            sort: field,
            asc: sortDirection === "asc",
        });
    };
    const getExcelUrl = () => {
        return `${YOUTUBE_KEYWORD_GENERATOR_EXCEL_ENDPOINT}?${fetchService.requestParams(apiParams)}`;
    };

    const transformData = (data) => {
        return {
            ...data,
            records: data.records.map((record) => ({
                ...record,
                volumeTrend: Object.keys(record.volumeTrend)
                    .sort()
                    .map((date) => ({
                        value: record.volumeTrend[date],
                        tooltip: (
                            <span>
                                <strong>{`${abbrNumberFilter()(record.volumeTrend[date])}`}</strong>
                                {` searches in ${dayjs.utc(date).format(shortMonthAndYearWithDelimiter)}`}
                            </span>
                        ),
                    })),
            })),
        };
    };

    const dataParamsAdapter = (args) => {
        //align page
        if (typeof args.page == "number") {
            args.page += 1;
        }
        return args;
    };

    return (
        <SWReactTableWrapper
            preventCountTracking={props.preventCountTracking}
            getDataCallback={getDataCallback}
            onDataError={props.onDataError}
            serverApi={YOUTUBE_KEYWORD_GENERATOR_ENDPOINT}
            requestBody={requestBody}
            onSort={onSort}
            tableColumns={columns}
            initialFilters={{ ...apiParams, ...paginationParams }}
            tableOptions={{
                noDataTitle: i18nFilter()("youtube.keyword.generator.table.nodata.title"),
                noDataSubtitle: i18nFilter()("youtube.keyword.generator.table.nodata.subtitle"),
            }}
            transformData={transformData}
            dataParamsAdapter={dataParamsAdapter}
            changePageCallback={changePageCallback}
            fetchServerPages={PAGES_TO_FETCH}
            rowsPerPage={ROWS_COUNT}
            pageIndent={0}
        >
            {(topComponentProps) => (
                <YoutubeKeywordGeneratorTableTop
                    {...topComponentProps}
                    columns={columns}
                    excelDownloadUrl={getExcelUrl()}
                    excelBodyForReq={requestBody}
                    totalRecords={totalRecords}
                />
            )}
        </SWReactTableWrapper>
    );
};
